/**
 * The structured company address every company must have: country, city, zip
 * code and address line 1 on the ABOUT profile. Address line 2 is optional and
 * the GPS pin (`geojson`) is derived — geocoded from the address, or the
 * country centroid as a fallback — so neither counts here.
 *
 * Lives in contracts so the backend (creation, profile PATCH validation, the
 * network-onboarding import) and the dashboard banner apply the same rule.
 */
export const REQUIRED_ADDRESS_DATA_POINT_IDS = [
  'country',
  'city',
  'zip_code',
  'address_line_1',
] as const;

export type RequiredAddressDataPointId =
  (typeof REQUIRED_ADDRESS_DATA_POINT_IDS)[number];

/**
 * ISO alpha-2 codes of countries with no postal-code system, where a zip code
 * cannot be asked for. Source: the Universal Postal Union's addressing
 * guidance as summarised in Wikipedia's "List of postal codes" (countries
 * listed without a code format), reviewed 2026-10.
 */
export const NO_POSTAL_CODE_COUNTRIES: ReadonlySet<string> = new Set([
  'AE',
  'AG',
  'AO',
  'AW',
  'BF',
  'BI',
  'BJ',
  'BO',
  'BS',
  'BW',
  'BZ',
  'CD',
  'CF',
  'CG',
  'CI',
  'CK',
  'CM',
  'DJ',
  'DM',
  'ER',
  'FJ',
  'GA',
  'GD',
  'GH',
  'GM',
  'GQ',
  'GY',
  'HK',
  'KI',
  'KM',
  'KN',
  'KP',
  'LY',
  'ML',
  'MO',
  'MR',
  'MW',
  'NR',
  'NU',
  'QA',
  'RW',
  'SB',
  'SC',
  'SL',
  'SR',
  'SS',
  'ST',
  'SY',
  'TD',
  'TG',
  'TK',
  'TL',
  'TO',
  'TV',
  'UG',
  'VU',
  'YE',
  'ZW',
]);

/** True unless the country is known to have no postal codes. */
export function isZipCodeRequired(country: unknown): boolean {
  if (typeof country !== 'string') return true;
  return !NO_POSTAL_CODE_COUNTRIES.has(country.trim().toUpperCase());
}

/** `null`, `undefined` and whitespace-only strings count as not filled in. */
export function isBlankAddressValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  return typeof value === 'string' && value.trim() === '';
}

type ProfileData = Readonly<
  Record<string, { value?: unknown } | null | undefined>
>;

/**
 * The required address data points a profile's `data` is missing, in
 * `REQUIRED_ADDRESS_DATA_POINT_IDS` order. Zip code is only required when the
 * profile's country uses postal codes.
 */
export function getMissingAddressFields(
  data: ProfileData | null | undefined,
): RequiredAddressDataPointId[] {
  const country = data?.country?.value;
  return REQUIRED_ADDRESS_DATA_POINT_IDS.filter(id => {
    if (id === 'zip_code' && !isZipCodeRequired(country)) return false;
    return isBlankAddressValue(data?.[id]?.value);
  });
}
